<template>
  <div>
    <header>
      <section class="hero">
        <div class="hero-body">
          <div class="container">
            <h1 class="title">UbiAnimate</h1>
            <h2 class="subtitle">
              Fast &amp; lightweight CSS animations for notifications
            </h2>
            <h3 class="subtitle">100% free &amp; open source</h3>
            <form>
              <fieldset id="enter">
                <div class="control has-icons-left">
                  <div class="select is-medium">
                    <AnimationSelect
                      direction="in"
                      v-model="enterAnimationSelect"
                    ></AnimationSelect>
                  </div>
                  <div class="icon is-left">
                    <i class="fas fa-hand-point-right"></i>
                  </div>
                </div>
                <div class="select is-medium">
                  <ChainableAnimationSelect
                    v-model="enterChainableAnimationSelect"
                  ></ChainableAnimationSelect>
                </div>
              </fieldset>
              <fieldset id="leave">
                <div class="control has-icons-left">
                  <div class="select is-medium">
                    <AnimationSelect
                      direction="out"
                      v-model="exitAnimationSelect"
                    ></AnimationSelect>
                  </div>
                  <div class="icon is-left">
                    <i class="fas fa-hand-point-left"></i>
                  </div>
                </div>
                <div class="select is-medium">
                  <ChainableAnimationSelect
                    v-model="exitChainableAnimationSelect"
                  ></ChainableAnimationSelect>
                </div>
              </fieldset>
              <fieldset id="trigger">
                <button
                  class="button is-primary"
                  @click.prevent="handleClick()"
                >
                  UbiAnimate!
                </button>
              </fieldset>
              <fieldset id="position">
                <div class="control has-icons-left">
                  <div class="select">
                    <PositionSelect
                      v-model="notificationPositionSelect"
                    ></PositionSelect>
                  </div>
                  <div class="icon is-left is-small">
                    <i class="fas fa-arrows-alt"></i>
                  </div>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
      </section>
    </header>
    <main>
      <section class="section">
        <div class="container">
          <div class="columns">
            <div class="column is-one-third is-hidden-touch">
              <div class="image">
                <img src="./assets/svg/usage.svg" />
              </div>
            </div>
            <div class="column">
              <h2 class="title">Usage</h2>
              <div class="columns">
                <div class="column">
                  <h3 class="subtitle">Entrance classes</h3>
                  <pre>{{ enterClasses }}</pre>
                </div>
                <div class="column">
                  <h3 class="subtitle">Exit classes</h3>
                  <pre>{{ leaveClasses }}</pre>
                </div>
              </div>
              <p>
                Have a play around with the demo above.
              </p>
              <p>
                Once you have found entrance and exit animations that you like,
                and in the position you intend to use, copy the Entrance classes
                &amp; Exit classes shown above and add them as instructed by
                your chosen notification software.
              </p>
              <p>
                Nice &amp; easy!
              </p>
            </div>
          </div>
        </div>
      </section>
      <section class="section has-background-white-bis">
        <div class="container">
          <div class="columns">
            <div class="column">
              <h2 class="title">Installation</h2>
              <div class="subsection">
                <h3 class="subtitle">
                  Via CDN
                </h3>
                <p>
                  The fastest &amp; easiest way to get up and running with
                  UbiAnimate is via
                  <a
                    href="https://www.jsdelivr.com/package/gh/rmhubbert/ubi-animate?path=dist"
                    >jsDelivr</a
                  >. Simply copy this URL into a LINK in the HEAD of your HTML
                  file.
                </p>
                <pre>
  https://cdn.jsdelivr.net/npm/ubi-animate/dist/ubianimate.min.css</pre
                >
              </div>
              <div class="subsection">
                <h3 class="subtitle">
                  Via Package Manager
                </h3>
                <p>
                  Install UbiAnimate directly into your projects with NPM or
                  Yarn.
                </p>
                <div class="columns">
                  <div class="column">
                    <h3 class="subtitle">NPM</h3>
                    <pre>npm install ubi-animate</pre>
                  </div>
                  <div class="column">
                    <h3 class="subtitle">Yarn</h3>
                    <pre>yarn add ubi-animate</pre>
                  </div>
                </div>
                <p>
                  Once installed, you can import the compiled CSS into your
                  projects with the following CSS import (don't forget to swap
                  out '/PATH/TO/NODE_MODULES' with the actual path to the
                  node_modules directory in your project) :
                </p>
                <pre>
  @import "/PATH/TO/NODE_MODULES/ubi-animate/dist/ubianimate.min.css";</pre
                >
                <p>
                  Alternatively, you can import the uncompiled Sass with the
                  following Sass import :
                </p>
                <pre>@import "~ubi-animate/src/ubianimate";</pre>
              </div>
              <div class="subsection">
                <h3 class="subtitle">
                  Via GitHub
                </h3>
                <p>
                  You can also clone, download or fork UbiAnimate directly from
                  the
                  <a href="https://github.com/rmhubbert/ubi-animate"
                    >UbiAnimate GitHub page</a
                  >.
                </p>
                <pre>
git clone https://github.com/rmhubbert/ubi-animate.git</pre
                >
              </div>
            </div>
            <div class="column is-one-third is-hidden-touch">
              <div class="image">
                <img src="./assets/svg/installation.svg" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="section">
        <div class="container">
          <div class="columns">
            <div class="column is-one-third is-hidden-touch">
              <div class="image">
                <img src="./assets/svg/customisation.svg" />
              </div>
            </div>
            <div class="column">
              <h2 class="title">Customisation</h2>
              <div class="subsection">
                <h3 class="subtitle">
                  Sass variables
                </h3>
                <p>
                  UbiAnimate can be customised simply by overriding Sass
                  variables.
                </p>
                <p>
                  Make sure, however, that you override these variables
                  <strong>before</strong> importing the uncompiled Sass as
                  outlined above.
                </p>
                <div class="table-container">
                  <SassVariablesTable></SassVariablesTable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    <footer>
      <div class="level">
        <div class="level-item">
          <a href="https://github.com/rmhubbert/ubi-animate"
            ><i class="fab fa-github"></i> GitHub</a
          >
        </div>
        <div class="level-item">
          <a href="https://www.npmjs.com/package/ubi-animate"
            ><i class="fab fa-npm"></i> npm</a
          >
        </div>
        <div class="level-item">
          <a href="https://yarnpkg.com/en/package/ubi-animate"
            ><i class="fab fa-yarn"></i> Yarn</a
          >
        </div>
      </div>
      <div class="level">
        <div class="level-item">
          <p>
            Illustrations courtesy of
            <a href="https://undraw.co/illustrations">UnDraw</a>. UbiAnimate is
            made available under the
            <a href="https://choosealicense.com/licenses/mit/">MIT License</a>.
            &copy; RM Hubbert 2019.
          </p>
        </div>
      </div>
    </footer>
    <UbiNotify v-bind="config"></UbiNotify>
  </div>
</template>

<script>
import AnimationSelect from "./components/AnimationSelect";
import ChainableAnimationSelect from "./components/ChainableAnimationSelect";
import PositionSelect from "./components/PositionSelect";
import SassVariablesTable from "./components/SassVariablesTable";
import UbiNotify from "./ubi-notify/components/UbiNotify";
import Quotes from "./data/quotes";

export default {
  name: "app",
  components: {
    AnimationSelect,
    ChainableAnimationSelect,
    PositionSelect,
    UbiNotify,
    SassVariablesTable
  },

  data() {
    return {
      quotes: [],
      enterAnimationSelect: "bounce in vval",
      enterChainableAnimationSelect: "pulsate",
      exitAnimationSelect: "bounce out hval",
      exitChainableAnimationSelect: "wave",
      notificationPositionSelect: "down left"
    };
  },

  computed: {
    enterClasses() {
      const classes = `animate ${this.cleanInput(
        this.enterAnimationSelect
      )}${this.calcDirection(this.enterAnimationSelect)} ${
        this.enterChainableAnimationSelect
      }`;
      return classes.trim();
    },

    leaveClasses() {
      const classes = `animate ${this.cleanInput(
        this.exitAnimationSelect
      )}${this.calcDirection(this.exitAnimationSelect, true)} ${
        this.exitChainableAnimationSelect
      }`;
      return classes.trim();
    },

    notificationPosition() {
      switch (this.notificationPositionSelect) {
        case "down right":
          return "top left";
        case "down center":
          return "top center";
        case "down left":
          return "top right";
        case "up right":
          return "bottom left";
        case "up center":
          return "bottom center";
        case "up left":
          return "bottom right";
        default:
          return "top right";
      }
    },

    config() {
      const conf = {};
      conf.name = "ubi";
      conf.position = this.notificationPosition;
      conf.stackFromTop = conf.position.includes("top");
      conf.cssFramework = "bulma";

      const animationConf = {};
      animationConf.enterActive = this.enterClasses;
      animationConf.leaveActive = this.leaveClasses;
      animationConf.move = "ubi-notify-transition-move-7";
      conf.animation = animationConf;

      return conf;
    }
  },

  methods: {
    handleClick() {
      if (this.quotes.length <= 1) this.initQuotes();
      const quote = this.quotes.pop();
      this.$notify(quote.quote, quote.author, "danger", "ubi");
    },

    calcDirection(directionFlag, reverse = false) {
      if (directionFlag.includes("fade")) return "";

      const dirs = this.notificationPositionSelect.split(" ");
      let vDir = dirs[0];
      let hDir = dirs[1];

      if (directionFlag.includes("hval")) vDir = "";
      else if (directionFlag.includes("vval")) hDir = "";

      if (reverse) {
        if (vDir === "up") vDir = "down";
        else if (vDir === "down") vDir = "up";
        if (hDir === "left") hDir = "right";
        else if (hDir === "right") hDir = "left";
      }

      let pos = `${vDir} ${hDir}`;

      return ` ${pos.trim()}`;
    },

    cleanInput(input) {
      return input.replace(/ vval| hval| dval/gi, "");
    },

    initQuotes() {
      this.quotes = Quotes.slice(); // We want a copy, not a reference
      this.quotes.sort(() => Math.random() - 0.5);
    }
  },

  created() {
    this.initQuotes();
  }
};
</script>

<style lang="scss">
@import "./assets/scss/app";
</style>
